import useViewportWidth from './useViewportWidth';
import useMounted from './useMounted';

/**
 * Returns true once mounted AND the viewport is confirmed to match
 * matchesWidth. Before mount, returns initialValue (default false), so
 * desktop-only content is absent from the DOM on SSR/first paint unless
 * initialValue says otherwise -- pair with a CSS breakpoint rule on the
 * "shown by default" side of the gate to avoid a mirrored flash there.
 *
 * Pass a server-detected deviceType-based guess as initialValue (e.g.
 * `deviceType === 'desktop'`) to avoid a real desktop visitor seeing
 * desktop-only content pop in only after hydration -- the guess must be
 * derivable identically during SSR and the first client render (before
 * the mount effect fires) to avoid a hydration mismatch, which is true
 * for anything sourced from the initial Redux state.
 *
 * Once mounted, live/resize-reactive matching takes over regardless of
 * initialValue, which matters when a component has more than one CSS
 * breakpoint tier (e.g. mobile / tablet / desktop) that can't be
 * captured by a single desktop-vs-not boolean.
 *
 * @param {(width: number) => boolean} matchesWidth - a predicate function
 *   such as `(width) => width >= 768`.
 * @param {boolean} [initialValue] - value to return before mount.
 */
export default function useViewportMatch(matchesWidth, initialValue = false) {
	const { viewportWidth } = useViewportWidth();
	const mounted = useMounted();

	return mounted ? matchesWidth(viewportWidth) : initialValue;
}
